import { logger } from "@/helpers/loggerHelper";

export const THANK_YOU_STORAGE_KEY = "thankYouAccessGrant";
export const THANK_YOU_ACCESS_TTL_MS = 60 * 1000;

type ThankYouAccessParams = {
  queryType?: string | null;
  stateFrom?: string | null;
};

type ThankYouAccessGrant = ThankYouAccessParams & {
  type: string;
  grantedAt: number;
  [key: string]: unknown;
};

export const THANK_YOU_ACTION_TYPES = {
  CAMPAIGN_PAUSE: "campaign_pause",
  ADJUST_BUDGET: "adjust_budget",
  EXTEND_CAMPAIGN: "extend_campaign",
  JOIN_CAMPAIGN: "join_campaign_confirm",
  RRD_JOIN: "rrd-join-campaign-confirm",
  NATIONAL_PLUS_UP: "national-plus-up-confirm",
  PACKAGE_SELECTION: "package_selection",
  MS_ACTIVATION: "ms_activation_brief",
  GENERIC: "generic",
};

const STATE_FROM_TO_ACTION_TYPE: Record<string, string> = {
  campaign_pause: THANK_YOU_ACTION_TYPES.CAMPAIGN_PAUSE,
  boost_campaign_confirm: THANK_YOU_ACTION_TYPES.ADJUST_BUDGET,
  extend_campaign: THANK_YOU_ACTION_TYPES.EXTEND_CAMPAIGN,
  join_campaign_confirm: THANK_YOU_ACTION_TYPES.JOIN_CAMPAIGN,
  "rrd-join-campaign-confirm": THANK_YOU_ACTION_TYPES.RRD_JOIN,
  "national-plus-up-confirm": THANK_YOU_ACTION_TYPES.NATIONAL_PLUS_UP,
  package_selection: THANK_YOU_ACTION_TYPES.PACKAGE_SELECTION,
  ms_activation_brief: THANK_YOU_ACTION_TYPES.MS_ACTIVATION,
};

// Survives React strict-mode remounts within the same page load, without
// keeping sessionStorage reusable after the user leaves the thank-you page.
let lastValidatedGrant: ThankYouAccessGrant | null = null;
let clearLastValidatedGrantFrame: number | null = null;

const cancelScheduledClear = () => {
  if (clearLastValidatedGrantFrame !== null) {
    cancelAnimationFrame(clearLastValidatedGrantFrame);
    clearLastValidatedGrantFrame = null;
  }
};

export const clearValidatedGrantCache = () => {
  cancelScheduledClear();
  lastValidatedGrant = null;
};

export const resetThankYouAccessState = () => {
  clearValidatedGrantCache();
  clearThankYouAccessGrant();
};

export const retainThankYouAccessValidation = () => {
  cancelScheduledClear();
};

export const releaseThankYouAccessValidation = () => {
  cancelScheduledClear();
  clearLastValidatedGrantFrame = requestAnimationFrame(() => {
    lastValidatedGrant = null;
    clearThankYouAccessGrant();
    clearLastValidatedGrantFrame = null;
  });
};

const readStoredGrant = (): ThankYouAccessGrant | null => {
  try {
    const raw = sessionStorage.getItem(THANK_YOU_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ThankYouAccessGrant) : null;
  } catch (error) {
    logger.error("Error reading thank-you access grant:", error);
    return null;
  }
};

export const resolveThankYouActionType = ({
  queryType,
  stateFrom,
}: ThankYouAccessParams = {}) => {
  if (queryType) {
    return queryType;
  }

  if (stateFrom) {
    return STATE_FROM_TO_ACTION_TYPE[stateFrom] || stateFrom;
  }

  return THANK_YOU_ACTION_TYPES.GENERIC;
};

const grantMatchesParams = (
  grant: ThankYouAccessGrant | null,
  { queryType, stateFrom }: ThankYouAccessParams
) => {
  if (!grant?.type) {
    return false;
  }

  const queryActionType = queryType
    ? resolveThankYouActionType({ queryType })
    : null;
  const stateActionType = stateFrom
    ? resolveThankYouActionType({ stateFrom })
    : null;

  if (queryActionType && grant.type !== queryActionType) {
    return false;
  }

  if (stateActionType && grant.type !== stateActionType) {
    return false;
  }

  if (
    queryActionType &&
    stateActionType &&
    queryActionType !== stateActionType
  ) {
    return false;
  }

  return true;
};

const grantIsFresh = (grant: ThankYouAccessGrant | null) => {
  if (!grant?.grantedAt) {
    return false;
  }

  return Date.now() - grant.grantedAt <= THANK_YOU_ACCESS_TTL_MS;
};

export const isThankYouHardReload = () => {
  const [navigationEntry] = performance.getEntriesByType("navigation");
  return (
    (navigationEntry as PerformanceNavigationTiming | undefined)?.type ===
    "reload"
  );
};

export const clearThankYouAccessGrant = () => {
  try {
    sessionStorage.removeItem(THANK_YOU_STORAGE_KEY);
  } catch (error) {
    logger.error("Error clearing thank-you access grant:", error);
  }
};

export const grantThankYouAccess = (
  actionType: string,
  metadata: Record<string, unknown> = {}
) => {
  try {
    cancelScheduledClear();
    lastValidatedGrant = null;
    const grant: ThankYouAccessGrant = {
      type: actionType,
      grantedAt: Date.now(),
      ...metadata,
    };
    sessionStorage.setItem(THANK_YOU_STORAGE_KEY, JSON.stringify(grant));
  } catch (error) {
    logger.error("Error granting thank-you access:", error);
  }
};

export const grantThankYouAccessForFlow = ({
  queryType,
  stateFrom,
}: ThankYouAccessParams = {}) => {
  grantThankYouAccess(resolveThankYouActionType({ queryType, stateFrom }), {
    queryType,
    stateFrom,
  });
};

/**
 * Validates client access to the thank-you page for the current browser tab.
 * URL query params alone are not trusted; they must match a grant written
 * immediately after a successful action in the same browser tab. The grant is
 * retained briefly so browser refresh keeps working after a legitimate flow.
 */
export const validateThankYouAccess = ({
  queryType,
  stateFrom,
}: ThankYouAccessParams = {}): ThankYouAccessGrant | null => {
  const params = { queryType, stateFrom };

  if (
    lastValidatedGrant &&
    grantMatchesParams(lastValidatedGrant, params) &&
    grantIsFresh(lastValidatedGrant)
  ) {
    return lastValidatedGrant;
  }

  const storedGrant = readStoredGrant();
  if (!grantMatchesParams(storedGrant, params)) {
    return null;
  }

  if (!grantIsFresh(storedGrant)) {
    clearThankYouAccessGrant();
    clearValidatedGrantCache();
    return null;
  }

  lastValidatedGrant = storedGrant;
  return storedGrant;
};
